import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Flex,
  Loader as AWSLoader,
  SelectField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  View
} from '@aws-amplify/ui-react';
import { useStyles } from './styles';
import { useParams } from 'react-router-dom';
import { getAppMeasurements, getScanImage } from '@/api';
import {
  formatDateWithTime,
  getFilteredScans,
  getScanMeasurements
} from '@/utils';
import { ScanStatus } from '@/constants';
import { Typography } from '@/components';
import { Tabs } from '@/components/Tabs';
import Select from 'rc-select';
import { IoIosArrowDown } from 'react-icons/io';
import { MdDone } from 'react-icons/md';
import { handleScanAction } from '@/utils/get-successful-scan';
import { handleAsyncAction } from '@/utils/handle-async-action';
import { useAppData } from '@/zustand/useAppData';
import { useAssetsData } from '@/zustand/useAssetsData';

const getScanStatus = (status: string) => {
  switch (status) {
    case ScanStatus.Complete:
      return 'Complete';
    case ScanStatus.Failed:
      return 'Failed';
    case ScanStatus.WaitingForData:
      return 'Waiting for Data';
    default:
      return 'Processing';
  }
};

enum TabLabels {
  Measurements = 'Measurements',
  ScanPreview = 'Scan Preview'
}

export const ScanMeasurements = () => {
  const styles = useStyles();
  const params = useParams();
  const { id } = useAppData((store) => store.appData);

  const {
    behavior_list: { scan_review_option_panel }
  } = useAssetsData((store) => store.assetsData);

  const show_avatar = !!scan_review_option_panel;

  const [scans, setScans] = useState([]);
  const [currentScanId, setCurrentScanId] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scanImage, setScanImage] = useState(null);
  const [measurementsLoading, setMeasurementsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [scansLoading, setScansLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(TabLabels.Measurements);

  const getScans = useCallback(async () => {
    await handleAsyncAction({
      setLoading: setScansLoading,
      setResult: (data) => {
        setScans(data);
        if (data?.length > 0) {
          setCurrentScanId(data[0].id);
        }
      },
      fetcher: () => getFilteredScans(params.customerId)
    });
  }, []);

  const getMeasurements = useCallback(async () => {
    await handleScanAction({
      scanId: currentScanId,
      scans,
      setLoading: setMeasurementsLoading,
      setResult: (res) => setMeasurements(getScanMeasurements(id, res?.output)),
      fetcher: getAppMeasurements
    });
  }, [currentScanId]);

  const getMeasurementsPreview = useCallback(async () => {
    await handleScanAction({
      scanId: currentScanId,
      scans,
      setLoading: setPreviewLoading,
      setResult: (res) => setScanImage(res?.output?.url),
      fetcher: getScanImage
    });
  }, [currentScanId]);

  useEffect(() => {
    (async () => {
      await getScans();
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await getMeasurements();
      await getMeasurementsPreview();
    })();
  }, [currentScanId]);

  return (
    <View className={styles.wrapper}>
      {scansLoading ? (
        <Flex justifyContent={'center'} alignItems={'center'}>
          <AWSLoader size="large" />
        </Flex>
      ) : scans.length > 0 ? (
        <>
          <label style={{ display: 'block' }}>Select Scan</label>
          <Select
            placeholder={'Select Scan'}
            defaultOpen={false}
            autoFocus
            value={currentScanId}
            choiceTransitionName="rc-select-selection__choice-zoom"
            suffixIcon={<IoIosArrowDown />}
            menuItemSelectedIcon={<MdDone />}
            dropdownMatchSelectWidth
            onChange={(value) => setCurrentScanId(value)}
            options={scans.map((scan) => ({
              label: `${formatDateWithTime(scan.date)} - ${getScanStatus(
                scan.status
              )}`,
              value: scan.id,
              disabled: scan.status !== ScanStatus.Complete
            }))}
          />

          {scanImage && (
            <Tabs
              labels={
                show_avatar
                  ? [TabLabels.Measurements, TabLabels.ScanPreview]
                  : [TabLabels.Measurements]
              }
              value={activeTab}
              onChange={setActiveTab}
            />
          )}

          {activeTab === TabLabels.Measurements ? (
            <View
              className={`${styles.tableWrapper} ${
                scanImage && styles.tableWithPreview
              }`}
            >
              {measurementsLoading && (
                <Flex
                  className={styles.loader}
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <AWSLoader size="large" />
                </Flex>
              )}
              <Table>
                <TableHead>
                  <TableRow style={{ display: 'flex' }}>
                    <TableCell style={{ width: '50%' }} as="th">
                      Name
                    </TableCell>
                    <TableCell style={{ width: '50%' }} as="th">
                      Scan Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {measurements &&
                    measurements.map(([key, value]: any) => (
                      <TableRow key={key} style={{ display: 'flex' }}>
                        <TableCell style={{ width: '50%' }}>{key}</TableCell>
                        <TableCell style={{ width: '50%' }}>{value}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </View>
          ) : (
            <>
              {show_avatar && scanImage && (
                <div className={styles.image}>
                  {previewLoading && (
                    <Flex
                      className={styles.loader}
                      justifyContent={'center'}
                      alignItems={'center'}
                    >
                      <AWSLoader size="large" />
                    </Flex>
                  )}
                  <img src={scanImage} />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {/*TODO change message if need*/}
          <Typography component={'p'}>You have no scans yet</Typography>
        </>
      )}
    </View>
  );
};
